<template>
  <div class="agent-page-wrapper">
    <div class="content-area">
      <div class="page-header">
        <div class="header-content">
          <div class="header-left">
            <v-btn
              icon
              variant="text"
              size="medium"
              class="back-btn"
              @click="goBack">
              <Icon name="arrow-left" variant="solid" size="20" color="default"/>
            </v-btn>
            <h1 class="page-title">
              {{ pageTitle }}
            </h1>
          </div>
          <div v-if="mode !== 'create'" class="header-right">
            <v-btn
              icon
              variant="text"
              size="small"
              color="primary"
              class="mank-btn-icon mank-btn-icon-md"
              @click="deleteAgent">
              <Icon name="trash" variant="solid" size="20"/>
            </v-btn>
            <v-btn
              v-if="mode === 'test'"
              icon
              variant="text"
              size="small"
              color="gray"
              class="mank-btn-icon mank-btn-icon-md"
              @click="switchToEditMode">
              <Icon name="edit" variant="solid" size="20"/>
            </v-btn>
          </div>
        </div>
      </div>

      <div class="main-content main-content--split-row agent-page-main">
        <div class="agent-split-inner">
          <div class="left-panel">
            <div class="form-content">
              <div class="form-group">
                <label class="form-label required">
                  Agent name
                  <span class="required-asterisk">*</span>
                </label>
                <MankTextField
                  v-model="agentName"
                  :placeholder="mode === 'create' ? 'Enter agent name' : 'Agent name'"
                  class="form-input"
                  :readonly="isReadonly"
                  hide-details="auto"/>
              </div>

              <div class="form-group agent-inline-controls">
                <div class="agent-inline-grid">
                  <div class="agent-inline-cell">
                    <label class="form-label required">
                      Voice
                      <span class="required-asterisk">*</span>
                    </label>
                    <MankSelect
                      v-model="selectedVoice"
                      :items="voices"
                      placeholder="Select voice"
                      density="compact"
                      :menu-props="{
                        maxHeight: 300,
                        offsetY: true,
                        nudgeBottom: 8,
                      }"
                      item-text="title"
                      item-value="value"
                      class="agent-form-select"
                      :disabled="isReadonly">
                      <template v-slot:append>
                        <Icon name="expand-down" variant="solid" color="muted"/>
                      </template>
                    </MankSelect>
                  </div>
                  <div class="agent-inline-cell">
                    <label class="form-label">Tools</label>
                    <MankSelect
                      v-model="selectedTools"
                      :items="tools"
                      placeholder="Select tools"
                      density="compact"
                      :menu-props="{
                        maxHeight: 300,
                        offsetY: true,
                        nudgeBottom: 8,
                      }"
                      item-text="title"
                      item-value="value"
                      multiple
                      class="agent-form-select"
                      :disabled="isReadonly">
                      <template v-slot:append>
                        <Icon name="expand-down" variant="solid" color="muted"/>
                      </template>
                    </MankSelect>
                  </div>
                  <div class="agent-inline-cell">
                    <label class="form-label">RAG</label>
                    <MankSelect
                      v-model="selectedRags"
                      :items="ragCollections"
                      placeholder="Select collections"
                      density="compact"
                      :menu-props="{
                        maxHeight: 300,
                        offsetY: true,
                        nudgeBottom: 8,
                      }"
                      item-text="title"
                      item-value="value"
                      multiple
                      class="agent-form-select"
                      :disabled="isReadonly">
                      <template v-slot:append>
                        <Icon name="expand-down" variant="solid" color="muted"/>
                      </template>
                    </MankSelect>
                  </div>
                  <div class="agent-inline-cell agent-inline-cell--settings">
                    <div class="agent-settings-icon-wrap">
                      <v-btn
                        icon
                        variant="text"
                        size="small"
                        type="button"
                        class="agent-settings-icon-btn mank-btn-icon mank-btn-icon-md"
                        :disabled="isReadonly"
                        @click="openSettingsDrawer">
                        <Icon name="settings" variant="solid" size="20" color="default"/>
                        <v-tooltip activator="parent" location="top">
                          Settings
                        </v-tooltip>
                      </v-btn>
                    </div>
                  </div>
                </div>
              </div>

              <div class="form-group">
                <label class="form-label">System prompt</label>
                <MankTextarea
                  v-model="systemPrompt"
                  placeholder="You are a helpful assistant."
                  variant="outlined"
                  rows="4"
                  class="form-input system-prompt-textarea"
                  :readonly="isReadonly"
                  hide-details="auto"/>
              </div>
            </div>
          </div>

          <div class="right-panel">
            <div class="form-group agent-test-stack">
              <h3 class="form-label agent-test-heading ds-panel-title">
                Test agent
              </h3>
              <div class="agent-test-card">
                <div class="agent-test-columns">
                  <span class="agent-test-col-title">Agent</span>
                  <span class="agent-test-col-title">User</span>
                </div>
                <div class="agent-test-body">
                  <div v-if="!isCallActive" class="test-empty-state">
                    <div class="test-empty-icon">
                      <Icon name="agent" variant="solid" color="primary" size="44"/>
                    </div>
                    <div class="test-empty-text">
                      <h4 class="test-empty-title">
                        Ready to test
                      </h4>
                      <p class="test-empty-description">
                        Use “Start a call” below to speak with this agent in the browser.
                      </p>
                    </div>
                  </div>
                  <div v-else class="call-interface">
                    <div class="call-active-state">
                      <div class="call-active-indicator">
                        <span class="live-dot"/>
                        <span class="live-label">Live</span>
                      </div>
                      <p class="call-active-title">
                        Call in progress
                      </p>
                      <p class="call-active-agent">
                        {{ agentData.name || 'Agent' }}
                      </p>
                      <p class="call-active-hint">
                        You can speak with the agent. Click “Stop a call” to end the test.
                      </p>
                    </div>
                  </div>
                </div>
                <div class="agent-test-actions">
                  <v-btn
                    :color="isCallActive ? 'primary' : undefined"
                    class="start-call-btn mank-btn"
                    :class="{ 'start-call-btn--active': isCallActive }"
                    elevation="0"
                    :outlined="!isCallActive"
                    block
                    :disabled="callLoading"
                    @click="toggleTest">
                    <v-icon
                      v-if="isCallActive"
                      size="20"
                      color="white"
                      class="mr-2">
                      mdi-stop
                    </v-icon>
                    <Icon
                      v-else
                      name="start-call"
                      variant="solid"
                      color="primary"
                      class="mr-2"/>
                    {{ isCallActive ? 'Stop a call' : 'Start a call' }}
                  </v-btn>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        v-if="mode === 'create' || mode === 'edit'"
        class="footer-buttons">
        <v-btn
          variant="text"
          size="medium"
          class="mank-btn"
          elevation="0"
          @click="mode === 'create' ? goBack() : cancelEdit()">
          <Icon name="cross" variant="solid" size="20" color="default"/>
          Cancel
        </v-btn>

        <v-btn
          v-if="mode === 'create'"
          variant="flat"
          color="primary"
          size="medium"
          class="mank-btn"
          elevation="0"
          :loading="loading"
          :disabled="loading || !agentData.name || !selectedVoice"
          @click="saveAgent">
          Create
        </v-btn>

        <v-btn
          v-else
          variant="flat"
          color="primary"
          size="medium"
          class="mank-btn"
          elevation="0"
          :loading="loading"
          :disabled="loading || !hasChanges || !agentData.name || !selectedVoice"
          @click="saveAgent">
          Save changes
        </v-btn>
      </div>
    </div>

    <!-- Settings Drawer -->
    <AgentSettingsDrawer
      :visible="showSettingsDrawer"
      :agent-data="agentData"
      @close="showSettingsDrawer = false"
      @apply="onSettingsApply"
      @cancel="onSettingsCancel"/>

    <DeleteConfirmationModal
      :visible="showDeleteDialog"
      @cancel="cancelDelete"
      @confirm="confirmDelete"/>
  </div>
</template>

<script>
import Icon from '@/components/Icon.vue';
import AgentSettingsDrawer from '@/components/AgentSettingsDrawer.vue';
import DeleteConfirmationModal from '@/components/DeleteConfirmationModal.vue';
import AgentService from '@/services/AgentService';
import VoiceService from '@/services/VoiceService';
import ToolService from '@/services/ToolService';
import CollectionService from '@/services/CollectionService';
import LivekitService from '@/services/LivekitService';
import { Room, RoomEvent } from 'livekit-client';

export default {
  name: 'AgentPage',
  components: { Icon, AgentSettingsDrawer, DeleteConfirmationModal },
  data() {
    return {
      loading: false,
      callLoading: false,
      isCallActive: false,
      roomRef: null,
      // Swagger: CreateAgentRequest fields
      agentData: {
        name: '',
        voice_id: '',
        agent_model_id: '',
        first_message: '',
        first_message_delay: 0,
        first_speaker: 'agent',
        temperature: 0.7,
        max_duration: 300,
        call_recording: false,
        tools: [], // Array of tool IDs
        rags: [], // Array of RAG collection IDs
        inactivity_messages: [],
        frame_activation_threshold: 0.5,
        minimum_interruption_duration: 0.5,
        minimum_turn_duration: 0.5,
        turn_endpoint_delay: 0.5,
        uninterruptible: false,
        join_timeout: 30,
        time_exceed_message: '',
        language_hint: 'en',
        initial_output_medium: 'audio',
      },
      showSettingsDrawer: false,
      showDeleteDialog: false,
      originalData: {},
      // Dropdown options
      voices: [],
      tools: [],
      ragCollections: [],
      // Selected values
      selectedVoice: null,
      selectedTools: [],
      selectedRags: [],
    };
  },
  computed: {
    mode() {
      // Route'a göre mode belirleme
      if (this.$route.name === 'CreateAgent') return 'create';
      if (this.$route.name === 'EditAgent') return 'edit';
      if (this.$route.name === 'TestAgent') return 'test';
      return 'create';
    },
    pageTitle() {
      switch (this.mode) {
      case 'create': return 'Create Agent';
      case 'edit': return 'Edit Agent';
      case 'test': return 'Test Agent';
      default: return 'Agent';
      }
    },
    isReadonly() {
      return this.mode === 'test';
    },
    hasChanges() {
      if (this.mode !== 'edit') return false;
      return JSON.stringify(this.agentData) !== JSON.stringify(this.originalData);
    },
    // Computed properties for form fields (backward compatibility)
    agentName: {
      get() {
        return this.agentData.name;
      },
      set(value) {
        this.agentData.name = value;
      },
    },
    systemPrompt: {
      get() {
        return this.agentData.first_message;
      },
      set(value) {
        this.agentData.first_message = value;
      },
    },
  },
  watch: {
    async $route() {
      await this.loadAgentData();
    },
    // Dropdown selections'ı agentData ile sync et
    selectedVoice(newValue) {
      this.agentData.voice_id = newValue;
    },
    selectedTools(newValue) {
      this.agentData.tools = newValue || [];
    },
    selectedRags(newValue) {
      this.agentData.rags = newValue || [];
    },
  },
  async mounted() {
    await this.loadDropdownData();
    await this.loadAgentData();
  },
  beforeUnmount() {
    if (this.roomRef) {
      this.roomRef.disconnect().catch(() => {});
      this.roomRef = null;
    }
  },
  methods: {
    /**
     * Dropdown verileri için API çağrıları
     */
    async loadDropdownData() {
      try {
        const [voicesRes, toolsRes, collectionsRes] = await Promise.all([
          VoiceService.getAllVoices(),
          ToolService.getAllTools(),
          CollectionService.getAllCollections(),
        ]);

        // Voices dropdown
        if (voicesRes?.success && voicesRes.data) {
          this.voices = voicesRes.data.map((voice) => ({
            title: voice.name,
            value: voice.id,
            subtitle: voice.description,
          }));
        }

        // Tools dropdown
        if (toolsRes?.success && toolsRes.data) {
          this.tools = toolsRes.data.map((tool) => ({
            title: tool.name,
            value: tool.id,
            subtitle: tool.description,
          }));
        }

        // RAG Collections dropdown
        if (collectionsRes?.success && collectionsRes.data) {
          this.ragCollections = collectionsRes.data.map((collection) => ({
            title: collection.name,
            value: collection.id,
            subtitle: collection.description,
          }));
        }
      } catch (error) {
        this.$store.dispatch('ui/showNotification', {
          type: 'error',
          title: 'Error',
          message: 'Failed to load dropdown data',
        });
      }
    },

    /**
     * Agent verilerini yükle
     * Swagger: GET /agent/{id} -> GetAgentResponse
     */
    async loadAgentData() {
      if (this.mode === 'create') {
        // Create mode - default values
        this.resetAgentData();
      } else if (this.$route.params.id) {
        // Edit/Test mode - API'den veri çek
        this.loading = true;
        try {
          const response = await AgentService.getAgentById(this.$route.params.id);

          if (response?.success && response.data) {
            const agent = response.data;
            // GetAgentResponse'u agentData'ya map et
            this.agentData = {
              name: agent.name || '',
              voice_id: agent.voice?.id || '',
              agent_model_id: agent.model?.id || '',
              first_message: agent.first_message || '',
              first_message_delay: agent.first_message_delay || 0,
              first_speaker: agent.first_speaker || 'agent',
              temperature: agent.temperature || 0.7,
              max_duration: agent.max_duration || 300,
              call_recording: agent.call_recording || false,
              tools: agent.tools?.map((t) => t.id) || [],
              rags: agent.rags?.map((r) => r.id) || [],
              inactivity_messages: agent.inactivity_messages || [],
              frame_activation_threshold: agent.frame_activation_threshold || 0.5,
              minimum_interruption_duration: agent.minimum_interruption_duration || 0.5,
              minimum_turn_duration: agent.minimum_turn_duration || 0.5,
              turn_endpoint_delay: agent.turn_endpoint_delay || 0.5,
              uninterruptible: agent.uninterruptible || false,
              join_timeout: agent.join_timeout || 30,
              time_exceed_message: agent.time_exceed_message || '',
              language_hint: agent.language_hint || 'en',
              initial_output_medium: agent.initial_output_medium || 'audio',
            };

            // Selected values for dropdowns
            this.selectedVoice = agent.voice?.id || null;
            this.selectedTools = agent.tools?.map((t) => t.id) || [];
            this.selectedRags = agent.rags?.map((r) => r.id) || [];

            // Store original data for change detection
            this.originalData = JSON.parse(JSON.stringify(this.agentData));
          } else {
            throw new Error(response?.message || 'Agent not found');
          }
        } catch (error) {
          this.$store.dispatch('ui/showNotification', {
            type: 'error',
            title: 'Error',
            message: error.message || 'Failed to load agent data',
          });
          this.$router.push('/agents');
        } finally {
          this.loading = false;
        }
      }
    },

    /**
     * Agent verilerini sıfırla (create mode için)
     */
    resetAgentData() {
      this.agentData = {
        name: '',
        voice_id: '',
        agent_model_id: '',
        first_message: 'Hello! How can I help you today?',
        first_message_delay: 0,
        first_speaker: 'agent',
        temperature: 0.7,
        max_duration: 300,
        call_recording: false,
        tools: [],
        rags: [],
        inactivity_messages: [],
        frame_activation_threshold: 0.5,
        minimum_interruption_duration: 0.5,
        minimum_turn_duration: 0.5,
        turn_endpoint_delay: 0.5,
        uninterruptible: false,
        join_timeout: 30,
        time_exceed_message: 'I apologize, but our time is up. Thank you for the conversation!',
        language_hint: 'en',
        initial_output_medium: 'audio',
      };
      this.selectedVoice = null;
      this.selectedTools = [];
      this.selectedRags = [];
      this.originalData = JSON.parse(JSON.stringify(this.agentData));
    },
    goBack() {
      if (this.hasChanges) {
        // TODO: Show confirmation dialog
        if (confirm('You have unsaved changes. Are you sure you want to leave?')) {
          this.$router.go(-1);
        }
      } else {
        this.$router.go(-1);
      }
    },

    deleteAgent() {
      if (!this.$route.params.id) return;
      this.showDeleteDialog = true;
    },
    cancelDelete() {
      this.showDeleteDialog = false;
    },
    /**
     * Agent silme onayı
     * Swagger: DELETE /agent/{id}
     */
    async confirmDelete() {
      const id = this.$route.params.id;
      if (!id) {
        this.showDeleteDialog = false;
        return;
      }
      this.loading = true;
      try {
        const response = await AgentService.deleteAgent(id);

        if (response?.success) {
          this.$store.dispatch('ui/showNotification', {
            type: 'success',
            title: 'Success',
            message: `Agent "${this.agentData.name}" deleted successfully`,
          });
          this.$router.push('/agents');
        } else {
          throw new Error(response?.message || 'Failed to delete agent');
        }
      } catch (error) {
        this.$store.dispatch('ui/showNotification', {
          type: 'error',
          title: 'Error',
          message: error.message || 'Failed to delete agent',
        });
      } finally {
        this.loading = false;
        this.showDeleteDialog = false;
      }
    },

    switchToEditMode() {
      this.$router.push(`/edit-agent/${this.$route.params.id}`);
    },

    /**
     * Agent oluşturma
     * Swagger: POST /agent with CreateAgentRequest
     */
    async createAgent() {
      if (!this.validateAgentData()) return;

      this.loading = true;
      try {
        // CreateAgentRequest formatına uygun veri hazırla
        const requestData = {
          ...this.agentData,
          voice_id: this.selectedVoice,
          tools: this.selectedTools,
          rags: this.selectedRags,
        };

        const response = await AgentService.createAgent(requestData);

        if (response?.success && response.data?.id) {
          this.$store.dispatch('ui/showNotification', {
            type: 'success',
            title: 'Success',
            message: `Agent "${this.agentData.name}" created successfully`,
          });
          // Test sayfasına yönlendir
          this.$router.push(`/test-agent/${response.data.id}`);
        } else {
          throw new Error(response?.message || 'Failed to create agent');
        }
      } catch (error) {
        this.$store.dispatch('ui/showNotification', {
          type: 'error',
          title: 'Error',
          message: error.message || 'Failed to create agent',
        });
      } finally {
        this.loading = false;
      }
    },

    cancelEdit() {
      this.$router.push({ name: 'Agents' });
    },

    /**
     * Agent güncelleme
     * Swagger: PUT /agent/{id} with UpdateAgentRequest
     */
    async saveAgent() {
      if (this.mode === 'create') {
        await this.createAgent();
        return;
      }

      if (!this.validateAgentData() || !this.$route.params.id) return;

      this.loading = true;
      try {
        // UpdateAgentRequest formatına uygun veri hazırla
        const requestData = {
          ...this.agentData,
          voice_id: this.selectedVoice,
          tools: this.selectedTools,
          rags: this.selectedRags,
        };

        const response = await AgentService.updateAgent(this.$route.params.id, requestData);

        if (response?.success) {
          this.$store.dispatch('ui/showNotification', {
            type: 'success',
            title: 'Success',
            message: `Agent "${this.agentData.name}" updated successfully`,
          });

          // Update original data
          this.originalData = JSON.parse(JSON.stringify(this.agentData));

          // Test sayfasına yönlendir
          this.$router.push(`/test-agent/${this.$route.params.id}`);
        } else {
          throw new Error(response?.message || 'Failed to update agent');
        }
      } catch (error) {
        this.$store.dispatch('ui/showNotification', {
          type: 'error',
          title: 'Error',
          message: error.message || 'Failed to update agent',
        });
      } finally {
        this.loading = false;
      }
    },

    /**
     * Form validasyonu
     */
    validateAgentData() {
      if (!this.agentData.name?.trim()) {
        this.$store.dispatch('ui/showNotification', {
          type: 'error',
          title: 'Validation Error',
          message: 'Agent name is required',
        });
        return false;
      }

      if (!this.selectedVoice) {
        this.$store.dispatch('ui/showNotification', {
          type: 'error',
          title: 'Validation Error',
          message: 'Voice selection is required',
        });
        return false;
      }

      return true;
    },
    /**
     * Test call başlat / durdur (agent listesi ile aynı mantık)
     */
    async toggleTest() {
      if (this.isCallActive) {
        await this.stopTest();
        return;
      }
      await this.startCall();
    },
    /**
     * Agent ile test call başlat (LiveKit + dispatch)
     */
    async startCall() {
      const agentId = this.$route.params.id;
      if (!agentId) return;

      const agent = { id: agentId, name: this.agentData.name || 'Agent' };

      try {
        this.isCallActive = true;
        this.callLoading = true;

        const now = new Date();
        const pad = (n) => String(n).padStart(2, '0');
        const timestamp = `${pad(now.getDate())}${pad(now.getMonth() + 1)}${now.getFullYear()}-${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
        const roomName = `room-${agent.id}-${timestamp}`;

        const tokenResp = await LivekitService.getToken(roomName);
        if (!tokenResp || !tokenResp.data) {
          throw new Error('LiveKit token alınamadı');
        }

        const { token, url } = tokenResp.data;
        const room = new Room();
        this.roomRef = room;

        room.on(RoomEvent.TrackSubscribed, (track) => {
          if (track.kind === 'audio') {
            const element = track.attach();
            document.body.appendChild(element);
          }
        });

        await room.connect(url, token);

        if (!navigator.mediaDevices?.getUserMedia) {
          throw new Error('Mikrofon erişimi mevcut değil. Lütfen sayfayı HTTPS ile açın veya tarayıcı izinlerini kontrol edin.');
        }
        await room.localParticipant.enableCameraAndMicrophone();

        await LivekitService.dispatchAgent({
          room: roomName,
          phone: 'WEB_TEST',
          agent_name: 'base-agent',
          agent_id: agent.id,
        });

        this.$store.dispatch('ui/showNotification', {
          type: 'success',
          title: 'Call Started',
          message: `${agent.name} ile test araması başlatıldı.`,
        });
      } catch (error) {
        console.error('Test failed:', error);
        this.isCallActive = false;
        this.$store.dispatch('ui/showNotification', {
          type: 'error',
          title: 'Error',
          message: error.message || 'Test başlatılamadı.',
        });
      } finally {
        this.callLoading = false;
      }
    },
    /**
     * Test call durdur
     */
    async stopTest() {
      if (this.roomRef) {
        try {
          await this.roomRef.disconnect();
        } catch (e) {
          console.warn('Room disconnect error:', e);
        }
        this.roomRef = null;
      }
      this.isCallActive = false;
      this.callLoading = false;
      this.$store.dispatch('ui/showNotification', {
        type: 'info',
        title: 'Call Ended',
        message: 'Test araması sonlandırıldı.',
      });
    },

    openSettingsDrawer() {
      this.showSettingsDrawer = true;
    },

    onSettingsApply(settings) {
      // Agent ayarlarını uygula
      Object.assign(this.agentData, settings);
      this.showSettingsDrawer = false;

      this.$store.dispatch('ui/showNotification', {
        type: 'success',
        title: 'Settings Applied',
        message: 'Agent settings have been updated',
      });
    },

    onSettingsCancel() {
      this.showSettingsDrawer = false;
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/styles/colors.scss';
@import '@/styles/variables.scss';

/*
 * Global .main-content--split-row = flex row; burada dış kabı column + scroll yapıyoruz.
 * İçte .agent-split-inner satır: sol form | sağ test; align-items stretch → aynı yükseklik (içerik max’ı).
 */
.content-area > .main-content.main-content--split-row.agent-page-main {
  flex-direction: column;
  align-items: stretch;
  overflow-y: auto;
  padding: 0;
  flex: 1;
  min-height: 0;
}

.agent-split-inner {
  display: flex;
  flex-direction: row;
  align-items: stretch;
  gap: 24px;
  padding: 24px;
  flex: 0 0 auto;
  width: 100%;
  box-sizing: border-box;
}

.footer-buttons {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 12px;
  padding: 12px 24px;
  border-top: 1px solid $light-border;
  background-color: $light-surface-2;
  flex-shrink: 0;

  .mank-btn {
    height: 32px;
    font-family: $body-font-family;
    font-size: $body-3-size;
    font-weight: 600;
    display: inline-flex;
    align-items: center;
    gap: 8px;
  }
}

.left-panel {
  flex: 1 1 0;
  min-width: 0;
}

.form-content {
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 8px;

  .form-label {
    font-family: $body-font-family;
    font-size: $body-3-size;
    font-weight: 600;
    color: $light-foreground-1;
    margin: 0;

    &.required .required-asterisk {
      color: $light-primary;
      font-weight: 700;
    }
  }
}

.form-content :deep(.form-input),
.form-content :deep(.agent-form-select),
.form-content :deep(.mank-input),
.form-content :deep(.mank-textarea) {
  width: 100%;
}

.agent-inline-controls {
  .agent-inline-grid {
    display: grid;
    grid-template-columns: minmax(0, 1.15fr) minmax(0, 1fr) minmax(0, 1fr) minmax(36px, auto);
    gap: 12px 16px;
    align-items: end;
  }

  .agent-inline-cell {
    display: flex;
    flex-direction: column;
    gap: 8px;
    min-width: 0;

    .form-label {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }

  .agent-inline-cell--settings {
    justify-self: start;
    max-width: 48px;
  }

  .agent-settings-icon-wrap {
    display: flex;
    align-items: center;
    min-height: 32px;
  }

  .agent-settings-icon-btn {
    margin-left: -4px;
  }
}

@media (max-width: 1264px) {
  .agent-inline-controls .agent-inline-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .agent-inline-controls .agent-inline-cell--settings {
    grid-column: 1 / -1;
    max-width: none;
    justify-self: start;
  }
}

@media (max-width: 700px) {
  .agent-inline-controls .agent-inline-grid {
    grid-template-columns: 1fr;
  }

  .agent-inline-controls .agent-inline-cell--settings {
    grid-column: auto;
    max-width: none;
  }

  .agent-split-inner {
    flex-direction: column;
  }

  .right-panel {
    flex: 1 1 auto;
    max-width: none;
  }
}

.right-panel {
  flex: 0 0 clamp(300px, 34vw, 400px);
  max-width: 420px;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.agent-test-stack {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  margin-bottom: 0;
}

.agent-test-heading {
  margin-top: 0;
  flex-shrink: 0;
}

.agent-test-card {
  flex: 1;
  min-height: 200px;
  background: $light-surface-1;
  border-radius: 12px;
  border: 1px solid $light-border;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.agent-test-columns {
  display: grid;
  grid-template-columns: 1fr 1fr;
  padding: 12px 16px;
  border-bottom: 1px solid $light-border;
  background: $light-surface-2;
}

.agent-test-col-title {
  font-family: $body-font-family;
  font-size: $body-2-size;
  font-weight: 700;
  color: $light-foreground-1;
  line-height: 1.2;
}

.agent-test-body {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 16px;
}

.test-empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  text-align: center;
}

.test-empty-icon {
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: $light-surface-2;
  border-radius: 50%;
}

.test-empty-text {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}

.test-empty-title {
  margin: 0;
  color: $light-foreground-1;
  font-family: $body-font-family;
  font-size: $body-2-size;
  font-weight: 700;
  line-height: 1.4;
}

.test-empty-description {
  margin: 0;
  color: $light-foreground-3;
  font-family: $body-font-family;
  font-size: $caption-1-size;
  font-weight: 400;
  line-height: 1.6;
  text-align: center;
  max-width: 280px;
}

.agent-test-actions {
  padding: 12px 16px;
  border-top: 1px solid $light-border;
  background: $light-surface-1;
}

.system-prompt-textarea {
  :deep(.v-input__slot) {
    align-items: stretch;
    min-height: 120px;
    border-radius: $radius-sm;
  }

  :deep(textarea) {
    min-height: 120px;

    &::placeholder {
      color: $light-foreground-3;
    }
  }
}

// Call active state (site ile uyumlu)
.call-interface {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 16px;
  box-sizing: border-box;
}

.call-active-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  text-align: center;
  max-width: 100%;
  padding: 20px;
  background: $light-surface-2;
  border: 1px solid $light-border;
  border-radius: $card-border-radius;
  box-shadow: 0 1px 3px rgba(23, 29, 34, 0.04);
}

.call-active-indicator {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  background: $light-success-light;
  border-radius: 200px;
  border: 1px solid $light-success-neutral;
}

.live-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: $light-success;
  animation: live-pulse 1.5s ease-in-out infinite;
}

.live-label {
  margin: 0;
  font-family: $body-font-family;
  font-size: $caption-1-size;
  font-weight: 600;
  color: $light-success-dark;
}

.call-active-title {
  margin: 0;
  font-family: $body-font-family;
  font-size: $body-2-size;
  font-weight: 700;
  color: $light-foreground-1;
  line-height: 1.4;
}

.call-active-agent {
  margin: 0;
  font-family: $body-font-family;
  font-size: $body-3-size;
  font-weight: 600;
  color: $light-primary;
  line-height: 1.4;
}

.call-active-hint {
  margin: 0;
  font-family: $body-font-family;
  font-size: $caption-1-size;
  font-weight: 400;
  color: $light-foreground-3;
  line-height: 1.5;
}

@keyframes live-pulse {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.7; transform: scale(1.1); }
}

.start-call-btn--active {
  :deep(.v-btn__content) {
    color: #fff;
  }
}
</style>
